// src/App.js
import React, { useState, useEffect } from 'react';
import { AppProvider, useApp } from './contexts/AppContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { useResponsive } from './hooks/useResponsive';
import Login from './components/Login';
import Sidebar from './components/layout/Sidebar';
import Topbar from './components/layout/Topbar';
import IdleAlerts from './components/layout/IdleAlerts';
import GeofenceAlerts from './components/layout/GeofenceAlerts';
import Dashboard from './components/dashboard/Dashboard';
import UserManagement from './components/users/UserManagement';
import ClientManagement from './components/clients/ClientManagement';
import TruckManagement from './components/trucks/TruckManagement';
import GPSManagement from './components/gps/GPSManagement';
import GPSLoadMonitoring from './components/gps/GPSLoadMonitoring';
import Documents from './components/documents/Documents';
import LiveTracking from './components/map/LiveTracking';
import GeofenceManagement from './components/geofencing/GeofenceManagement';
import TravelHistory from './components/history/TravelHistory';
import Reports from './components/reports/Reports';
import MyFleet from './components/clients/MyFleet';
import Settings from './components/settings/Settings';

const PAGE_COMPONENTS = {
  dashboard: Dashboard, users: UserManagement, clients: ClientManagement,
  trucks: TruckManagement, gps: GPSManagement, gpsLoads: GPSLoadMonitoring, documents: Documents,
  tracking: LiveTracking, geofencing: GeofenceManagement, history: TravelHistory, reports: Reports,
  my_trucks: MyFleet, live_map: LiveTracking, settings: Settings,
};

function AppShell() {
  const { currentUser, authLoading, focusTruckRequest } = useApp();
  const { theme } = useTheme();
  const { isMobile, isTablet } = useResponsive();

  // All hooks before any conditional returns
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activePage, setActivePage] = useState('dashboard');

  // Close the mobile drawer automatically whenever the viewport grows back
  // past the mobile breakpoint, so it doesn't stay stuck open on rotate/resize.
  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  // A notification's "View Truck" link sets focusTruckRequest — jump to the
  // right page for that role so LiveTracking can pick it up and select the
  // truck (Live Tracking for admin/dispatcher, Live Map for clients).
  useEffect(() => {
    if (!focusTruckRequest) return;
    const trackingPage = currentUser?.role === 'client' ? 'live_map' : 'tracking';
    setActivePage(trackingPage);
    if (isMobile) setMobileMenuOpen(false);
  }, [focusTruckRequest, currentUser, isMobile]);

  const handleNavigate = (page) => {
    setActivePage(page);
    if (isMobile) setMobileMenuOpen(false); // auto-close drawer after picking a page
  };

  if (authLoading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: theme.bgApp, flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 40, height: 40, border: `3px solid ${theme.border}`, borderTopColor: '#f97316', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        <div style={{ color: theme.textMuted, fontSize: 13 }}>Loading ShipDoTrack...</div>
        <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      </div>
    );
  }

  if (!currentUser) return <Login />;

  const PageComponent = PAGE_COMPONENTS[activePage] || Dashboard;
  const pageProps = activePage === 'my_trucks' ? { onNavigateToMap: () => setActivePage('live_map') } : {};

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: theme.bgApp, position: 'relative' }}>

      {/* Desktop / tablet: sidebar sits inline in the flex row.
          Mobile: sidebar becomes a fixed slide-in drawer, hidden by default. */}
      {isMobile ? (
        <>
          {/* Backdrop overlay — click to close */}
          {mobileMenuOpen && (
            <div
              onClick={() => setMobileMenuOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, animation: 'fadeIn 0.2s ease' }}
            />
          )}
          <div style={{
            position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 1101,
            transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.25s ease',
            boxShadow: mobileMenuOpen ? '8px 0 32px rgba(0,0,0,0.4)' : 'none',
          }}>
            <Sidebar
              activePage={activePage}
              onNavigate={handleNavigate}
              collapsed={false}
              onToggle={() => setMobileMenuOpen(false)}
              isMobileDrawer
            />
          </div>
        </>
      ) : (
        <Sidebar
          activePage={activePage}
          onNavigate={handleNavigate}
          collapsed={sidebarCollapsed && !isTablet}
          onToggle={() => setSidebarCollapsed(p => !p)}
        />
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <Topbar
          activePage={activePage}
          isMobile={isMobile}
          onOpenMenu={() => setMobileMenuOpen(true)}
        />
        <main style={{
          flex: 1,
          overflowY: 'auto',
          padding: isMobile ? '16px 14px' : isTablet ? '20px' : '24px 28px',
          background: theme.bgApp,
        }}>
          <PageComponent {...pageProps} />
        </main>
      </div>

      {/* Global idle alert popups */}
      <IdleAlerts />
      <GeofenceAlerts />
      <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes pulseRing { 0% { transform: scale(0.6); opacity: 0.9; } 100% { transform: scale(1.8); opacity: 0; } }
        @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
        @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
        @keyframes slideInRight { from { opacity:0; transform:translateX(40px); } to { opacity:1; transform:translateX(0); } }
        @keyframes modalIn { from { opacity:0; transform:scale(0.95); } to { opacity:1; transform:scale(1); } }
        button:hover:not(:disabled) { opacity: 0.88; }
        select option { background: var(--bgInput); color: var(--textPrimary); }
        .leaflet-control-zoom { border:1px solid var(--border) !important; background:var(--bgCard) !important; border-radius:8px !important; overflow:hidden; }
        .leaflet-control-zoom a { background:var(--bgCard) !important; color:var(--textMuted) !important; border-bottom:1px solid var(--border) !important; }
        .leaflet-control-zoom a:hover { background:var(--bgHover) !important; color:var(--textPrimary) !important; }
        .leaflet-popup-content-wrapper { background:var(--bgCard) !important; border:1px solid var(--borderInput) !important; border-radius:10px !important; color:var(--textPrimary) !important; }
        .leaflet-popup-tip { background:var(--bgCard) !important; }
        .leaflet-container { font-family:'DM Sans',sans-serif; }
        /* Smoothly glide truck markers to their new position on each GPS
           update instead of snapping instantly — Leaflet positions markers
           via a CSS transform, so animating that transform is enough to
           get fluid movement with no extra plugin needed. Scoped to just
           the live tracking map so it doesn't slow down click-to-place
           pins on the geofencing/route-planning maps elsewhere. */
        .sdt-live-map .leaflet-marker-icon { transition: none; } /* movement is now animated manually along the actual road path, not via CSS — a CSS transition here would fight with that and cause stuttering */

        /* Mobile-friendly tap targets and text sizing */
        @media (max-width: 640px) {
          input, select, textarea { font-size: 16px !important; } /* prevents iOS auto-zoom on focus */
          body { -webkit-text-size-adjust: 100%; }
        }
      `}</style>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AppProvider>
        <AppShell />
      </AppProvider>
    </ThemeProvider>
  );
}
